// src/components/VideoPlayer.js
import React, { useRef, useEffect, useState } from 'react';
import './VideoPlayer.css';

function VideoPlayer({ videoFilename, videoKey, onVideoEnd, onVideoStart, muted }) {
  const videoRef = useRef(null);
  const hasStartedRef = useRef(false);
  const videoUrl = `${process.env.REACT_APP_VIDEO_BASE_URL}/${videoFilename}`;

  // 'loading' - видео загружается в фоне
  // 'playing' - видео проигрывается
  // 'ended'   - видео проигралось
  // 'error'   - ошибка
  const [playerState, setPlayerState] = useState('loading');

  console.log(`VideoPlayer (${videoKey}): Rendering. Source: ${videoUrl}, State: ${playerState}`);

  useEffect(() => {
    // Этот useEffect будет управлять загрузкой видео
    let objectUrl = null; // Переменная для хранения Blob URL
    const abortController = new AbortController(); // Для отмены fetch при размонтировании
    const MIN_FIXATION_MS = 4000;

    const loadVideo = async () => {
      console.log(`VideoPlayer (${videoKey}): Starting background fetch for ${videoUrl}`);
      setPlayerState('loading'); // Устанавливаем состояние загрузки
      const startTime = performance.now();

      try {
        const response = await fetch(videoUrl, { signal: abortController.signal });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const videoBlob = await response.blob();
        objectUrl = URL.createObjectURL(videoBlob);

        if (videoRef.current) {
          videoRef.current.src = objectUrl;
          videoRef.current.load(); // Готовим плеер с новым источником
          const loadingMs = performance.now() - startTime;
          const waitMs = Math.max(0, MIN_FIXATION_MS - loadingMs);
          console.log(`VideoPlayer (${videoKey}): Video fetched and Blob URL created. Ready to play.`);
          setTimeout(() => {
            // Теперь, когда видео полностью в памяти, оно готово к запуску
            videoRef.current.play().then(() => {
              setPlayerState('playing');
              console.log(`VideoPlayer (${videoKey}): Playback started.`);
            }).catch(error => {
              console.error(`VideoPlayer (${videoKey}): Auto-play failed.`, error);
              setPlayerState('error'); // Если даже локальное видео не может запуститься
            });
          }, waitMs);
        }
      } catch (err) {
        const elapsed = performance.now() - startTime;
        const waitMs = isFinite(elapsed) ? Math.max(0, MIN_FIXATION_MS - elapsed) : MIN_FIXATION_MS;
        setTimeout(() => {
          if (err.name === 'AbortError') {
            console.log(`VideoPlayer (${videoKey}): Fetch aborted.`);
          } else {
            console.error(`VideoPlayer (${videoKey}): Error fetching video file.`, err);
            setPlayerState('error');
          }
        }, waitMs);
      }
    };

    loadVideo();

    // Очистка при размонтировании или смене videoKey
    return () => {
      abortController.abort(); // Отменяем fetch, если он еще не завершен
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl); // Освобождаем память от Blob URL
        console.log(`VideoPlayer (${videoKey}): Blob URL revoked.`);
      }
    };
  }, [videoKey, videoUrl]); // Зависимость только от ключа и URL видео

  // Этот useEffect следит за стартом и окончанием видео
  useEffect(() => {
    const currentVideoElement = videoRef.current;
    if (!currentVideoElement) return;
	
	hasStartedRef.current = false;

    const handlePlaying = () => {
      console.log(`VideoPlayer (${videoKey}): Event 'playing'.`);
      if (!hasStartedRef.current) {
	hasStartedRef.current = true;
        onVideoStart && onVideoStart(videoKey);
      }
    };

    const handleEnded = () => {
      console.log(`VideoPlayer (${videoKey}): Event 'ended'.`);
      setPlayerState('ended');
      onVideoEnd && onVideoEnd(videoKey);
    };

    currentVideoElement.addEventListener('playing', handlePlaying);
    currentVideoElement.addEventListener('ended', handleEnded);

    return () => {
      currentVideoElement.removeEventListener('playing', handlePlaying);
      currentVideoElement.removeEventListener('ended', handleEnded);
    };
  }, [videoKey, onVideoEnd, onVideoStart]);

  return (
    <div className="video-player-wrapper">
      {playerState === 'loading' && (
        <div className="fixation-point-container">
          <div className="fixation-point">•</div>
        </div>
      )}
      
      {playerState === 'error' && (
        <div className="video-error-container">
          <p>Ошибка загрузки видео.</p>
        </div>
      )}
      
      <video
        ref={videoRef}
        // Атрибут 'controls' убран, чтобы не было панели управления
        className={playerState === 'loading' || playerState === 'error' ? 'video-hidden' : ''}
        key={videoKey || videoFilename}
        playsInline
        muted={muted}
      >
        {/* Источник будет установлен через fetch и Blob URL */}
      </video>
      
      {playerState === 'ended' && (
        <div className="video-overlay-ended">Видео просмотрено</div>
      )}
    </div>
  );
}

export default VideoPlayer;